// 📄 src/pages/campaigns/AssignContactsPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import { toast } from "react-toastify";
import Modal from "react-modal";
import TagFilterDropdown from "./components/TagFilterDropdown";

// CSV campaign flow (schema → upload → map → validate → dry-run → commit)
import CsvAudienceSection from "./components/CsvAudienceSection";
import { fetchCsvSchema, getCampaignAudience } from "./api/csvApi";
// function shortId(id = "") {
//   return id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
// }

// function CampaignHeader({ campaign }) {
//   if (!campaign) {
//     return (
//       <header className="mb-6 rounded-[10px] border bg-white shadow-sm">
//         <div className="px-4 py-4">
//           <div className="h-3 w-20 animate-pulse rounded-[10px] bg-gray-200" />
//           <div className="mt-2 h-6 w-48 animate-pulse rounded-[10px] bg-gray-200" />
//           <div className="mt-2 h-3 w-72 animate-pulse rounded-[10px] bg-gray-200" />
//         </div>
//       </header>
//     );
//   }

//   const name =
//     campaign.name || campaign.campaignName || campaign.title || "(unnamed)";
//   const id = campaign.id || campaign.campaignId || "";

//   const copyId = async () => {
//     try {
//       if (!id) return;
//       await navigator.clipboard.writeText(id);
//       toast.success("Campaign ID copied");
//     } catch {
//       toast.error("Could not copy Campaign ID");
//     }
//   };

//   return (
//     <header className="mb-6 rounded-[10px] border bg-white shadow-sm">
//       <div className="px-4 py-4">
//         {/* Eyebrow */}
//         <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
//           Campaign
//         </div>

//         {/* Title row */}
//         <div className="mt-0.5 flex items-center justify-between gap-3">
//           <h1 className="truncate text-xl font-semibold text-gray-900">
//             {name}
//           </h1>
//           {id ? (
//             <button
//               onClick={copyId}
//               className="inline-flex items-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
//               aria-label="Copy campaign ID"
//               title="Copy Campaign ID"
//             >
//               <svg
//                 viewBox="0 0 24 24"
//                 className="h-4 w-4"
//                 fill="none"
//                 stroke="currentColor"
//                 strokeWidth="1.8"
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//               >
//                 <rect x="9" y="9" width="13" height="13" rx="2" />
//                 <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
//               </svg>
//               <span className="hidden sm:inline">Copy ID</span>
//               <span className="sm:hidden">Copy</span>
//             </button>
//           ) : null}
//         </div>

//         {/* Subtitle */}
//         <p className="mt-1 text-xs text-gray-500">
//           Assign recipients from CSV or include people from your CRM.
//         </p>

//         {/* Optional tiny ID chip (read-only) */}
//         {id ? (
//           <div className="mt-2 text-[11px] text-gray-500">
//             <span className="rounded-[10px] bg-gray-100 px-2 py-0.5">
//               ID: {shortId(id)}
//             </span>
//           </div>
//         ) : null}
//       </div>
//     </header>
//   );
// }

// Template Perview
if (typeof document !== "undefined" && process.env.NODE_ENV !== "test") {
  Modal.setAppElement("#root");
}

export default function AssignContactsPage() {
  const { id: campaignId } = useParams();
  const navigate = useNavigate();

  // ── Campaign + schema
  const [campaign, setCampaign] = useState(null);
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [audienceInfo, setAudienceInfo] = useState(null);

  // ── CRM panel state
  const [activeAudienceTab, setActiveAudienceTab] = useState("csv");

  // ── CRM contacts & UI
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const [tags, setTags] = useState([]);

  // ── CSV → CRM quick-import modal
  const [showAssignConfirmModal, setShowAssignConfirmModal] = useState(false);
  const [assignDraft, setAssignDraft] = useState({
    validIds: [],
    selectedCount: 0,
    hasExistingCsv: false,
  });
  const [selectedCrmCountSnapshot, setSelectedCrmCountSnapshot] = useState(0);
  const [csvSchemaSummary, setCsvSchemaSummary] = useState(null);
  const [csvSchemaNotice, setCsvSchemaNotice] = useState(null);
  const [schemaNoticeDismissed, setSchemaNoticeDismissed] = useState(false);

  const crmSupportsCurrentTemplate = placeholderCount === 0;
  const hasExistingCsv = !!(
    audienceInfo?.attachmentId ??
    audienceInfo?.AttachmentId
  );
  const schemaNoticeKey = `${csvSchemaNotice?.title ?? ""}:${csvSchemaNotice?.detail ?? ""}`;

  const getPhone = c => c.phoneNumber || c.phone || "";

  useEffect(() => {
    setSchemaNoticeDismissed(false);
  }, [schemaNoticeKey]);

  // Load campaign + CSV schema
  useEffect(() => {
    (async () => {
      if (!campaignId) {
        toast.error("No campaign ID found in URL.");
        return;
      }
      try {
        const res = await axiosClient.get(`/campaign/${campaignId}`);
        setCampaign(res.data);
      } catch (err) {
        console.error("Failed to load campaign:", err);
        toast.error("Failed to load campaign");
      }

      try {
        const sc = await fetchCsvSchema(campaignId);
        const n = Number(sc?.placeholderCount ?? 0);
        setPlaceholderCount(Number.isNaN(n) ? 0 : n);
      } catch {
        setPlaceholderCount(0);
      }

      try {
        const ai = await getCampaignAudience(campaignId);
        setAudienceInfo(ai ?? null);
      } catch {
        setAudienceInfo(null);
      }
    })();
  }, [campaignId]);

  useEffect(() => {
    if (!crmSupportsCurrentTemplate && activeAudienceTab === "crm") {
      setActiveAudienceTab("csv");
    }
  }, [crmSupportsCurrentTemplate, activeAudienceTab]);

  // Load CRM contacts (only when CRM tab is active and template supports it).
  useEffect(() => {
    if (activeAudienceTab !== "crm" || !crmSupportsCurrentTemplate) {
      setContacts([]);
      setFilteredContacts([]);
      setSelectedIds([]);
      return;
    }
    (async () => {
      try {
        let res;
        if (tags.length > 0) {
          res = await axiosClient.post("/contacts/filter-by-tags", tags);
          setContacts(res.data?.data || []);
        } else {
          res = await axiosClient.get("/contacts", {
            params: { tab: "all", page: 1, pageSize: 1000 },
          });
          setContacts(res.data?.data?.items || []);
        }
      } catch {
        toast.error("Failed to load contacts");
      }
    })();
  }, [activeAudienceTab, crmSupportsCurrentTemplate, tags]);

  // Local search filter for CRM list
  useEffect(() => {
    if (activeAudienceTab !== "crm" || !crmSupportsCurrentTemplate) return;
    const q = search.trim().toLowerCase();
    const result = contacts.filter(
      c =>
        c.name?.toLowerCase().includes(q) || getPhone(c).includes(search.trim())
    );
    setFilteredContacts(result);
  }, [activeAudienceTab, crmSupportsCurrentTemplate, contacts, search]);

  // CSV → quick import into CRM list
  // Selection helpers
  const toggleContact = id =>
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

  const toggleSelectAll = () => {
    const filteredIds = filteredContacts.map(c => c.id);
    const allSelected = filteredIds.every(id => selectedIds.includes(id));
    setSelectedIds(prev =>
      allSelected
        ? prev.filter(id => !filteredIds.includes(id))
        : [...new Set([...prev, ...filteredIds])]
    );
  };

  const allVisibleSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every(c => selectedIds.includes(c.id));
  const selectedCrmReadyCount = selectedIds.filter(id =>
    contacts.find(c => c.id === id && getPhone(c).trim() !== "")
  ).length;
  const visibleReadyCount = filteredContacts.filter(
    c => getPhone(c).trim() !== ""
  ).length;
  useEffect(() => {
    if (activeAudienceTab === "crm" && crmSupportsCurrentTemplate) {
      setSelectedCrmCountSnapshot(selectedCrmReadyCount);
    }
  }, [
    activeAudienceTab,
    crmSupportsCurrentTemplate,
    selectedCrmReadyCount,
  ]);

  // Assign selected CRM contacts
  const assignContacts = async () => {
    if (!campaign || !campaign.id) {
      toast.error("Campaign not ready. Please try again.");
      return;
    }
    let csvAudiencePresent = hasExistingCsv;
    try {
      const latestAudience = await getCampaignAudience(campaign.id);
      setAudienceInfo(latestAudience ?? null);
      csvAudiencePresent = !!(
        latestAudience?.attachmentId ??
        latestAudience?.AttachmentId
      );
    } catch {
      // Keep previously known state if refresh fails.
    }
    if (!crmSupportsCurrentTemplate) {
      toast.warn("CRM contacts are available only for phone-only templates.");
      return;
    }
    const validIds = selectedIds.filter(id =>
      contacts.find(c => c.id === id && getPhone(c).trim() !== "")
    );
    if (selectedIds.length > 0 && validIds.length === 0) {
      toast.warn("No selected contacts have valid phone numbers.");
      return;
    }
    if (validIds.length === 0 && !csvAudiencePresent) {
      toast.warn(
        "Please select CRM contacts or upload a CSV audience before continuing."
      );
      return;
    }

    setAssignDraft({
      validIds,
      selectedCount: selectedIds.length,
      hasExistingCsv: csvAudiencePresent,
    });
    setShowAssignConfirmModal(true);
  };

  const confirmAssignContacts = async () => {
    if (!campaign || !campaign.id) {
      setShowAssignConfirmModal(false);
      return;
    }

    try {
      if (assignDraft.validIds.length > 0) {
        const payload = { contactIds: assignDraft.validIds };
        await axiosClient.post(`/campaign/${campaign.id}/assign-contacts`, payload);
        toast.success("Contacts assigned to campaign.");
      } else {
        toast.success(
          "Saved. Your uploaded CSV audience will be used without additional CRM contacts."
        );
      }
      setShowAssignConfirmModal(false);
      navigate("/app/campaigns/template-campaigns-list", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Something went wrong during assignment.";
      toast.error(msg);
    }
  };

  const campaignDisplayName =
    campaign?.name ||
    campaign?.campaignName ||
    campaign?.title ||
    "this campaign";

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#f5f6f7]">
      <div className="mx-auto max-w-5xl px-4 py-5 sm:px-5">
        <div className="mb-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3 font-['Plus_Jakarta_Sans']">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600 shadow-sm shadow-emerald-200">
                <svg
                  className="h-5 w-5 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7h8m-8 5h8m-8 5h5M6 4h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z"
                  />
                </svg>
              </div>

              <div>
                <h1 className="text-xl font-bold text-slate-900">
                  Assign Contacts
                </h1>
                <p className="text-xs text-slate-500">
                  Add recipients from CSV or CRM for {campaignDisplayName}.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/app/campaigns/template-campaigns-list")}
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/70 bg-white/90 px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm transition hover:border-slate-200 hover:text-slate-900"
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back
            </button>
          </div>
        </div>
      {/* Title */}
      <h1 className="hidden mb-2 flex items-center gap-2 text-2xl font-bold text-emerald-700">
        📇 Assign Contacts
      </h1>

      {/* Campaign meta */}
      {campaign ? (
        <div className="hidden mb-6 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
            <strong className="font-semibold">Campaign:</strong>
            <span>
              {campaign?.name ||
                campaign?.campaignName ||
                campaign?.title ||
                "(unnamed campaign)"}
            </span>
          </span>
          {/* {campaign.templateName || campaign.messageTemplate ? ( */}
          {/* <span className="inline-flex items-center gap-2 rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-1 text-gray-700"> */}
          {/* <strong className="font-semibold">Template:</strong> */}
          {/* <span>{campaign.templateName || campaign.messageTemplate}</span> */}
          {/* </span> */}
          {/* ) : null} */}

        </div>
      ) : null}
      <section className="overflow-hidden rounded-[10px] border border-white/70 bg-white/95 p-3 sm:p-4">
        <div className="mb-3 flex items-center justify-start border-b border-slate-200 pb-2.5">
          <div className="inline-flex rounded-[10px] border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setActiveAudienceTab("csv")}
              className={`rounded-lg px-3 py-1.5 text-sm transition ${
                activeAudienceTab === "csv"
                  ? "bg-white font-semibold text-emerald-700 shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              CSV Upload
            </button>
            <button
              type="button"
              onClick={() =>
                crmSupportsCurrentTemplate && setActiveAudienceTab("crm")
              }
              disabled={!crmSupportsCurrentTemplate}
              className={`rounded-lg px-3 py-1.5 text-sm transition ${
                activeAudienceTab === "crm"
                  ? "bg-white font-semibold text-emerald-700 shadow-sm"
                  : "text-slate-600 hover:text-slate-800"
              } disabled:cursor-not-allowed disabled:opacity-50`}
              title={
                crmSupportsCurrentTemplate
                  ? "Select contacts from CRM"
                  : "CRM is available only for phone-only templates"
              }
            >
              CRM Contacts
            </button>
          </div>
        </div>

        {activeAudienceTab === "csv" && campaignId && (
          <div>
            <CsvAudienceSection
              campaignId={campaignId}
              campaign={campaign}
              selectedCrmCount={selectedCrmCountSnapshot}
              onSchemaSummaryChange={setCsvSchemaSummary}
              onSchemaGuardChange={setCsvSchemaNotice}
              schemaSummary={csvSchemaSummary}
              schemaNotice={
                csvSchemaNotice && !schemaNoticeDismissed ? csvSchemaNotice : null
              }
              onDismissSchemaNotice={() => setSchemaNoticeDismissed(true)}
            />
          </div>
        )}

        {activeAudienceTab === "crm" && crmSupportsCurrentTemplate && (
          <div className="border-t border-slate-100 pt-3">
            <div className="rounded-[8px] border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 xl:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)] xl:items-end">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Search Contacts
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                        <svg
                          viewBox="0 0 24 24"
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="7" />
                          <path d="m20 20-3.5-3.5" />
                        </svg>
                      </span>
                      <input
                        className="w-full rounded-[10px] border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-700 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
                        type="text"
                        placeholder="Search by name or phone..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <TagFilterDropdown
                      selectedTags={tags}
                      onChange={setTags}
                      category="All"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
                    Contacts shown: {filteredContacts.length}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
                    Valid numbers: {visibleReadyCount}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700">
                    Selected: {selectedIds.length}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    WhatsApp ready: {selectedCrmReadyCount}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.14em]">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em]">
                      Contact
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em]">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em]">
                      Tags
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em]">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.map(c => (
                    <tr
                      key={c.id}
                      className={`border-t border-slate-100 transition hover:bg-slate-50/80 ${
                        selectedIds.includes(c.id) ? "bg-emerald-50/40" : ""
                      }`}
                    >
                      <td className="px-4 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(c.id)}
                          onChange={() => toggleContact(c.id)}
                        />
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="min-w-0">
                          <div className="truncate font-medium text-slate-900">
                            {c.name || "Unnamed"}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-700">
                          {getPhone(c) || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="truncate text-xs text-slate-600">
                          {(c.tags || c.contactTags || [])
                            .map(t => t.tagName || t.name)
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {getPhone(c).trim() !== "" ? (
                          <span className="text-xs font-medium text-emerald-700">
                            Ready
                          </span>
                        ) : (
                          <span className="text-xs font-medium text-amber-700">
                            Missing Phone
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredContacts.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-14 text-center"
                      >
                        <div className="mx-auto max-w-sm">
                          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                            <svg
                              viewBox="0 0 24 24"
                              className="h-6 w-6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <circle cx="11" cy="11" r="7" />
                              <path d="m20 20-3.5-3.5" />
                            </svg>
                          </div>
                          <div className="mt-3 text-sm font-medium text-slate-700">
                            {tags.length > 0
                              ? "No contacts match the selected tags."
                              : "No contacts to show."}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            Try clearing filters, changing the search term, or
                            importing contacts into CRM.
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 rounded-[16px] border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">
                  {selectedIds.length}
                </span>{" "}
                selected across CRM.
                <span className="ml-1">
                  <span className="font-semibold text-emerald-700">
                    {selectedCrmReadyCount}
                  </span>{" "}
                  are ready for WhatsApp delivery.
                </span>
              </div>

              <button
                onClick={assignContacts}
                className="inline-flex items-center justify-center rounded-[12px] bg-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-emerald-200 transition hover:bg-emerald-700"
              >
                Assign Selected Contacts to Campaign
              </button>
            </div>
          </div>
        )}

        {activeAudienceTab === "crm" && !crmSupportsCurrentTemplate && (
          <div className="rounded-[10px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            CRM contacts are hidden for this campaign because the selected
            template needs dynamic values. Use the CSV tab to provide both phone
            and parameter columns.
          </div>
        )}
      </section>

      {/* CSV -> CRM mapping modal */}
      <Modal
        isOpen={showAssignConfirmModal}
        onRequestClose={() => setShowAssignConfirmModal(false)}
        className="mx-auto mt-20 max-w-xl rounded-[10px] bg-white p-6 shadow-lg"
        overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      >
        <h2 className="mb-2 text-lg font-bold text-gray-900">
          Confirm Recipient Setup
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          Do you want to save and continue with this campaign audience selection?
        </p>

        <div className="space-y-2 rounded-[10px] border bg-gray-50 p-3 text-sm text-gray-700">
          <div>
            CRM contacts selected:{" "}
            <strong>{assignDraft.validIds.length}</strong>
          </div>
          <div>
            CSV audience uploaded:{" "}
            <strong>{assignDraft.hasExistingCsv ? "Yes" : "No"}</strong>
          </div>
        </div>

        {assignDraft.hasExistingCsv && assignDraft.validIds.length > 0 && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            You selected recipients from both sources: uploaded CSV and CRM contacts.
            If you continue, both sets will be used for this campaign.
          </div>
        )}

        {assignDraft.hasExistingCsv && assignDraft.validIds.length === 0 && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            You already uploaded a CSV audience, but no CRM contacts are selected.
            If you continue, the campaign will proceed with CSV recipients only.
          </div>
        )}

        {!assignDraft.hasExistingCsv && assignDraft.validIds.length > 0 && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            You selected CRM contacts, but no CSV audience is uploaded. If you
            continue, the campaign will proceed with CRM recipients only.
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            className="text-gray-600 hover:underline"
            onClick={() => setShowAssignConfirmModal(false)}
          >
            Cancel
          </button>
          <button
            className="rounded-md bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700"
            onClick={confirmAssignContacts}
          >
            Yes, Save & Continue
          </button>
        </div>
      </Modal>

      </div>
    </div>
  );
}

